import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message,
  Upload
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  LoadingOutlined,
  CameraOutlined
} from '@ant-design/icons';
import associationApi from '../../api/associationApi';
import EditAssociationProfile from './EditAssociationProfile';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const AssociationProfile = () => {
  const navigate = useNavigate();
  const brandColor = '#3C007A';
  const [loading, setLoading] = useState(true);
  const [associationData, setAssociationData] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Default fallback images
  const defaultLogo = 'https://ui-avatars.com/api/?name=Association&background=3C007A&color=fff&size=128&bold=true';
  const defaultBanner = 'https://images.unsplash.com/photo-1560439514-4e9645039924?q=80&w=2070&auto=format&fit=crop';

  // Helper function to get image URL from server
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchAssociationProfile();
  }, []);

  const fetchAssociationProfile = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      if (userInfo.association) {
        const data = await associationApi.getAssociationProfile(userInfo.association);
        setAssociationData(data);
      } else {
        message.error('No association ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load association profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('logo', file);

      await associationApi.updateAssociationProfile(userInfo.association, formData);
      message.success('Logo uploaded successfully');
      fetchAssociationProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload logo');
      return false;
    }
  };

  const handleBannerUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      formData.append('banner', file);

      await associationApi.updateAssociationProfile(userInfo.association, formData);
      message.success('Banner uploaded successfully');
      fetchAssociationProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload banner');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'verified': 'success',
      'pending': 'warning',
      'active': 'success',
      'approved': 'success',
      'inactive': 'default',
      'rejected': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      'approved': 'Verified',
      'pending': 'Pending',
      'rejected': 'Rejected',
      'active': 'Active',
      'verified': 'Verified',
      'inactive': 'Inactive'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  const handleEditProfile = () => {
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    fetchAssociationProfile();
  };

  const logoUrl = associationData?.logo ? getImageUrl(associationData.logo) : defaultLogo;
  const bannerUrl = associationData?.banner ? getImageUrl(associationData.banner) : defaultBanner;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!associationData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No association data available. Please contact support.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Banner Section */}
        <div style={{
          height: 'clamp(200px, 30vh, 320px)',
          background: `url(${bannerError ? defaultBanner : bannerUrl}) center center / cover no-repeat`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }}>
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(to bottom, rgba(60, 0, 122, 0.3), rgba(0,0,0,0.6))'
          }} />

          {/* Hidden image to check if banner loads */}
          <img 
            src={bannerUrl} 
            style={{ display: 'none' }}
            onError={() => {
              console.error('Banner failed to load:', bannerUrl);
              setBannerError(true);
            }}
            onLoad={() => console.log('Banner loaded successfully')}
            alt="banner"
          />

          {/* Upload Banner Button */}
          <Upload
            showUploadList={false}
            beforeUpload={handleBannerUpload}
            accept="image/*"
          >
            <Button
              icon={<CameraOutlined />}
              style={{ position: 'absolute', top: 20, right: 20, zIndex: 2 }}
            >
              Change Banner
            </Button>
          </Upload>

          {/* Edit Profile Button */}
          <Button
            icon={<EditOutlined />}
            type="primary"
            size="large"
            onClick={handleEditProfile}
            style={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              zIndex: 10,
              fontSize: '16px',
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              height: '44px',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '8px',
              backgroundColor: brandColor,
              borderColor: brandColor,
              boxShadow: '0 4px 12px rgba(60, 0, 122, 0.3)',
            }}
          >
            Edit Profile
          </Button>
        </div>

        {/* Content Container */}
        <div style={{
          padding: '0 24px 24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  position: 'relative',
                  display: 'inline-block'
                }}>
                  <div style={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {logoError || !associationData?.logo ? (
                      <Avatar
                        size={130}
                        style={{
                          backgroundColor: brandColor,
                          fontSize: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                        icon={<TeamOutlined />}
                      >
                        {associationData?.associationName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <img
                        src={logoUrl}
                        alt="Association Logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '8px'
                        }}
                        onError={() => {
                          console.error('Logo failed to load:', logoUrl);
                          setLogoError(true);
                        }}
                        onLoad={() => console.log('Logo loaded successfully')}
                      />
                    )}
                  </div>

                  {/* Upload Logo Button */}
                  <Upload
                    showUploadList={false}
                    beforeUpload={handleLogoUpload}
                    accept="image/*"
                  >
                    <Button
                      icon={<CameraOutlined />}
                      shape="circle"
                      style={{
                        position: 'absolute',
                        bottom: 5,
                        right: 5,
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Upload>
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {associationData.associationName || 'Association Name'}
                  </Title>
                  <Space wrap>
                    <Tag
                      color="purple"
                      style={{
                        margin: 0,
                        fontSize: '16px',
                        padding: '4px 12px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400
                      }}
                    >
                      Association
                    </Tag>
                    {associationData.isVerified && (
                      <Tag
                        icon={<SafetyCertificateOutlined />}
                        color="success"
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        Verified
                      </Tag>
                    )}
                    {associationData.status && (
                      <Tag
                        color={getStatusColor(associationData.status)}
                        style={{
                          fontSize: '16px',
                          padding: '4px 12px',
                          fontFamily: "'Rubik', sans-serif",
                          fontWeight: 400
                        }}
                      >
                        {getStatusText(associationData.status)}
                      </Tag>
                    )}
                  </Space>
                  {associationData.website && (
                    <Link
                      href={associationData.website}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '16px',
                        fontFamily: "'Rubik', sans-serif",
                        fontWeight: 400,
                        color: "#333"
                      }}
                    >
                      <GlobalOutlined /> {associationData.website}
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            {/* Left Column: Association Details */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor }}>
                    <TeamOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Association Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{
                    fontWeight: 400,
                    color: '#666',
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    textAlign: 'center'
                  }}
                  contentStyle={{
                    fontSize: '17px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor,
                    wordBreak: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  <Descriptions.Item label="Association Name">
                    <Text strong style={{ fontSize: 18, fontFamily: "'Rubik', sans-serif", fontWeight: 400, color: brandColor, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      {associationData.associationName || 'N/A'}
                    </Text>
                  </Descriptions.Item>

                  <Descriptions.Item label="President Name">
                    <Space>
                      <Text strong style={{ fontSize: 17, fontFamily: "'Rubik', sans-serif", fontWeight: 400 }}>
                        {associationData.presidentName || 'N/A'}
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Registration Number">
                    {associationData.registrationNumber || 'Not Specified'}
                  </Descriptions.Item>

                  <Descriptions.Item label="Contact Person">
                    {associationData.contactPerson || 'N/A'}
                  </Descriptions.Item>

                  {associationData.designation && (
                    <Descriptions.Item label="Designation">
                      {associationData.designation}
                    </Descriptions.Item>
                  )}

                  {associationData.city && (
                    <Descriptions.Item label="City">
                      {associationData.city}
                    </Descriptions.Item>
                  )}

                  {associationData.state && (
                    <Descriptions.Item label="State">
                      {associationData.state}
                    </Descriptions.Item>
                  )}

                  {associationData.pincode && (
                    <Descriptions.Item label="Pincode">
                      {associationData.pincode}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    About
                  </Space>
                }
                bordered={false}
                style={{ marginTop: 24 }}
              >
                <Text
                  style={{
                    display: 'block',
                    lineHeight: 1.8,
                    fontSize: '16px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: '#333'
                  }}
                >
                  {associationData.description || associationData.about ||
                    `${associationData.associationName || 'This association'} is a leading organization dedicated to promoting industry growth and collaboration.`}
                </Text>
              </Card>
            </Col>

            {/* Right Column: Contact Information */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{
                    fontSize: '20px',
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 400,
                    color: brandColor
                  }}>
                    <EnvironmentOutlined style={{ color: brandColor }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                headStyle={{
                  borderBottom: 'none',
                  padding: '12px 12px 0 12px'
                }}
                bodyStyle={{
                  padding: '24px'
                }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />
                  
                  {/* Contact Person */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Contact Person
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {associationData.contactPerson || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MailOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666', textAlign: 'left' }}>
                        Email Address
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500, textAlign: 'left' }}>
                        {associationData.email || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      backgroundColor: '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PhoneOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 400, fontFamily: "'Rubik', sans-serif", color: '#666' }}>
                        Mobile Number
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                        {associationData.phone || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <Divider style={{ margin: '0px 0' }} />

                  {/* Location - Centered Layout */}
                  <div style={{ textAlign: 'center', marginTop: 0 }}>
                    <Text type="secondary" style={{ fontSize: 18, marginBottom: 8, display: 'block' }}>
                      Location
                    </Text>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'flex-start' }}>
                      <EnvironmentOutlined style={{ color: brandColor, marginTop: 4 }} />
                      <Text style={{
                        fontSize: 14,
                        color: '#333',
                        maxWidth: '80%',
                        lineHeight: 1.5
                      }}>
                        {associationData.address || 'Location not specified'}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditAssociationProfile
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSuccess={handleEditSuccess}
      />
    </ConfigProvider>
  );
};

export default AssociationProfile;
